import { useMutation } from '@apollo/client';
import { Alert, AlertIcon, Box, Flex, Image, Text } from '@chakra-ui/react';
import { useAtom } from 'jotai';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FaCcMastercard, FaCcVisa } from 'react-icons/fa6';
import { IoIosInformationCircleOutline, IoIosWarning } from 'react-icons/io';

import { LeaseFriendsLogo } from '../../assets/Images';
import { useCreateFinixPaymentInstrumentTokenResolverMutation } from '../../gql/generated/graphql';
import {
  createFinixPaymentMerchantGql,
  verifyFinixPaymentInstrumentGql,
} from '../../gql/referrersGql';
import PayoutAccountDetails from '../../pages/dashboard/components/referral/PayoutAccountDetails';
import { DealWithFarthestState } from '../../pages/dashboard/utils';
import { logger } from '../../services/sentry';
import { customerReferrer } from '../../store/atom/referrer';
import { ExpiryStatus, getCardExpiryStatus } from '../../utils/payouts';
import {
  CreateFinixPaymentMerchant,
  FinixPaymentInstrumentToken,
  VerifyFinixPaymentInstrument,
} from '../../utils/types/referrer';
import Modal from '../Modal';
import Button from '../ui/buttons/Button';

type Props = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  deal: DealWithFarthestState | undefined;
};

const PaymentInstrument = ({ isOpen, setIsOpen, deal }: Props) => {
  const [referrer, setReferrer] = useAtom(customerReferrer);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [expiry, setExpiry] = useState<ExpiryStatus>({
    expiring: false,
    timeframe: null,
  });
  const [showAddCardForm, setShowAddCardForm] = useState<boolean>(false);
  const [verifyFinixPaymentInstrumentMutation] = useMutation(verifyFinixPaymentInstrumentGql);
  const [createFinixPaymentMerchantMutation] = useMutation(createFinixPaymentMerchantGql);
  const [createFinixPaymentInstrumentTokenMutation] =
    useCreateFinixPaymentInstrumentTokenResolverMutation({
      context: {
        isErrorHandled: true,
      },
    });
  const onModalClose = () => setIsOpen(false);

  const verifyFinixPaymentInstrument = async (fields: VerifyFinixPaymentInstrument) => {
    try {
      if (!referrer || !referrer.finix_identity) return null;
      const data: VerifyFinixPaymentInstrument = { ...fields };
      const response = await verifyFinixPaymentInstrumentMutation({
        variables: {
          ...data,
        },
      });
      const { id } = response.data?.verifyFinixPaymentInstrumentResolver || {};
      if (id) {
        setReferrer((prev) => (prev ? { ...prev, finix_verify: id } : prev));
        return id;
      }
    } catch (err) {
      logger.error('PaymentInstrument', 'verifyFinixPaymentInstrument failed', err, null);
    }
    return null;
  };

  const handleVerifyInstrument = async (id?: string) => {
    if (!referrer) return;
    await verifyFinixPaymentInstrument({
      instrument: id || referrer?.finix_instrument || '',
      identity: referrer?.finix_identity || '',
    });
  };

  const createFinixPaymentMerchant = async (fields: CreateFinixPaymentMerchant) => {
    try {
      if (!referrer || !referrer.finix_identity) return null;
      const data: CreateFinixPaymentMerchant = { ...fields };
      const response = await createFinixPaymentMerchantMutation({
        variables: {
          ...data,
        },
      });
      const { id } = response.data?.createFinixPaymentMerchantResolver || {};
      if (id) {
        setReferrer((prev) => (prev ? { ...prev, finix_merchant: id } : prev));
        return id;
      }
    } catch (err) {
      logger.error('PaymentInstrument', 'createFinixPaymentMerchant failed', err, null);
    }
    return null;
  };

  const handleCreateMerchant = async () => {
    if (!referrer || referrer.finix_merchant) return;
    await createFinixPaymentMerchant({
      identity: referrer?.finix_identity || '',
    });
  };

  const createFinixPaymentInstrumentToken = async (fields: FinixPaymentInstrumentToken) => {
    try {
      if (!referrer || !referrer.finix_identity) return null;
      const data: FinixPaymentInstrumentToken = { ...fields, identity: referrer.finix_identity };
      const response = await createFinixPaymentInstrumentTokenMutation({
        variables: {
          ...data,
        },
      });
      const rsp = response?.data?.createFinixPaymentInstrumentTokenResolver;
      if (rsp) {
        const { id, pi_lf, pi_t, pi_b, pi_em, pi_ey } = rsp;
        setReferrer((prev) =>
          prev
            ? {
                ...prev,
                finix_instrument: id,
                instrument_brand: pi_b,
                instrument_type: pi_t,
                instrument_exp_mo: pi_em,
                instrument_exp_yr: pi_ey,
                instrument_last_four: pi_lf,
              }
            : prev,
        );
        return id;
      }
    } catch (err) {
      const e = err as Error;
      logger.error('PaymentInstrument', 'createFinixPaymentInstrumentToken failed', err, null);
      setTokenError(e.message);
    }
    return null;
  };

  useEffect(() => {
    setShowAddCardForm(referrer && !referrer.finix_instrument);
    if (referrer.instrument_exp_mo && referrer.instrument_exp_yr) {
      setExpiry(
        getCardExpiryStatus(Number(referrer.instrument_exp_mo), Number(referrer.instrument_exp_yr)),
      );
    }
  }, [referrer]);

  return (
    <Modal isOpen={isOpen} onClose={onModalClose} size="2xl">
      <Flex flexDir="column" mx={4} justifyContent="center">
        <Flex alignItems="center" gap={2}>
          <Image src={LeaseFriendsLogo} w={150} />
          <Text pt={1} fontSize={16} fontWeight={700}>
            Payment Account Information
          </Text>
        </Flex>
        {showAddCardForm && (
          <Alert
            status={tokenError ? 'error' : 'info'}
            rounded="md"
            mt={6}
            color={tokenError ? 'rgb(153, 25, 25)' : 'rgb(23, 61, 166)'}
            fontSize={12}
            fontWeight={600}
          >
            <AlertIcon as={tokenError ? IoIosWarning : IoIosInformationCircleOutline} />
            <Flex>
              {tokenError ? (
                <>{tokenError}</>
              ) : (
                <>We accept Visa and Mastercard Debit Cards for Lease Friends Referral Program</>
              )}
            </Flex>
          </Alert>
        )}
      </Flex>
      {showAddCardForm ? (
        <PayoutAccountDetails
          deal={deal}
          createToken={createFinixPaymentInstrumentToken}
          createMerchant={handleCreateMerchant}
          verifyInstrument={handleVerifyInstrument}
          cancelForm={referrer.finix_instrument ? () => setShowAddCardForm(false) : undefined}
        />
      ) : (
        <Flex justifyContent="space-between" p={8} mx={8} mt={8} rounded="md" borderWidth={1}>
          <Flex flexDir="column">
            <Flex alignItems="center" gap={2}>
              <Flex fontSize={24}>
                {referrer.instrument_brand === 'VISA' ? <FaCcVisa /> : <FaCcMastercard />}
              </Flex>
              {referrer.instrument_type && referrer.instrument_type !== 'UNKNOWN' && (
                <Text>{referrer.instrument_type}</Text>
              )}
              <Box fontSize={16}>
                ending in{' '}
                <Text as="span" fontWeight={700}>
                  {referrer.instrument_last_four}
                </Text>
              </Box>
            </Flex>
            <Box alignItems="center" gap={2} fontSize={14} color="#888">
              expires {referrer.instrument_exp_mo}/{referrer.instrument_exp_yr}
            </Box>
            {expiry.expiring && (
              <Alert
                p={0}
                px={1}
                rounded="sm"
                fontWeight={700}
                fontSize={12}
                status="error"
                color="rgb(197,48,48)"
              >
                expiring in {expiry.timeframe || ''}
              </Alert>
            )}
          </Flex>
          <Flex>
            <Button onClick={() => setShowAddCardForm(true)}>Replace</Button>
          </Flex>
        </Flex>
      )}
    </Modal>
  );
};

export default PaymentInstrument;
